<script setup lang="ts">
import { type RouteLocationRaw, RouterLink } from "vue-router";

interface LinkProps {
    type?: "primary" | "neutral" | "error" | "neutral-alt";
    size?: "md" | "sm" | "xs";
    underline?: boolean;

    leftIcon?: string;
    rightIcon?: string;
    disabled?: boolean;

    label?: string;

    to?: RouteLocationRaw;

    nobackground?: boolean;
    noHover?: boolean;
    noPointer?: boolean;
    noPadding?: boolean;
}

withDefaults(defineProps<LinkProps>(), {
    type: "primary",
    size: "md",
});

const emit = defineEmits(["click"]);
</script>
<template>
    <div :class="['ds-link', type, size, { underline }, { disabled }, { noHover }, { noPointer }, { noPadding }]">
        <RouterLink v-if="to" :to="to" class="ds-link-content">
            <i v-if="leftIcon" :class="`ti ti-${leftIcon}`"></i>
            <span>
                <slot>{{ label }}</slot>
            </span>
            <i v-if="rightIcon" :class="`ti ti-${rightIcon}`"></i>
        </RouterLink>
        <button v-else :class="['ds-link-content', { nobackground }]" :disabled="disabled" @click="emit('click', $event)">
            <i v-if="leftIcon" :class="`ti ti-${leftIcon}`"></i>
            <span>
                <slot>{{ label }}</slot>
            </span>
            <i v-if="rightIcon" :class="`ti ti-${rightIcon}`"></i>
        </button>
    </div>
</template>

<style lang="scss" scoped>
@mixin linkStyle($class, $baseColor, $darkColor) {
    color: $baseColor;
    @at-root .ds-link:not(.disabled, .noHover):hover#{$class}#{&} {
        color: $darkColor;
    }
}

.ds-link {
    width: fit-content;
    white-space: nowrap;
    padding: 0 4px;

    .ds-link-content {
        padding: 0;
        font-weight: 500;
        background-color: rgba(0, 0, 0, 0);
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 6px;
        position: relative;
    }

    &.underline {
        .ds-link-content::before {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            height: 1px;
            width: 100%;

            @at-root .primary#{&} {
                background-color: $blue-base;
            }
            @at-root .neutral#{&} {
                background-color: $text-main-900;
            }
            @at-root .error#{&} {
                background-color: $red-base;
            }

            @at-root .ds-link:not(.disabled):hover.primary#{&} {
                background-color: $blue-dark;
            }
            @at-root .ds-link:not(.disabled):hover.neutral#{&} {
                background-color: $text-sub-600;
            }
            @at-root .ds-link:not(.disabled):hover.error#{&} {
                background-color: $red-dark;
            }
        }
    }

    &.primary > .ds-link-content {
        @include linkStyle(#{".primary"}, $blue-base, $blue-dark);
    }
    &.neutral > .ds-link-content {
        @include linkStyle(#{".neutral"}, $text-main-900, $text-sub-600);
    }
    &.neutral-alt > .ds-link-content {
        @include linkStyle(#{".neutral-alt"}, $text-sub-600, $text-main-900);
    }
    &.error {
        padding: 0;
    }
    &.error > .ds-link-content {
        @include linkStyle(#{".error"}, $red-base, $red-dark);
    }

    &.disabled {
        pointer-events: none;
        .ds-link-content {
            cursor: not-allowed;
            color: $text-disabled-300;
        }
        color: $text-disabled-300;
    }

    &.md {
        .ds-link-content {
            @include font-size-14;
            i {
                width: 18px;
                height: 18px;
                font-size: 18px;
            }
        }
    }
    &.sm {
        .ds-link-content {
            @include font-size-13;
            i {
                width: 16px;
                height: 16px;
                font-size: 16px;
            }
        }
    }
    &.xs {
        .ds-link-content {
            @include font-size-12;
            a {
                gap: 4px;
            }
            i {
                width: 14px;
                height: 14px;
                font-size: 14px;
            }
        }
    }

    .nobackground {
        background-color: transparent;
    }

    &.noPointer {
        cursor: default;
        .ds-link-content {
            cursor: default;
        }
    }

    &.noPadding {
        padding: 0;
    }
}
</style>
